import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import html2canvas from 'html2canvas';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';

import { objectToQueryString, apiHelper, baseUrl } from 'helpers/util';
import { notify } from 'helpers/globalFunctions';
import { DateTime } from 'luxon';

import ColumnBox from 'components/ColumnBox';
import RowBox from 'components/RowBox';
import CardList from 'components/CardList';
import CardItem from 'components/CardItem';
import SmallestHeading from 'components/SmallestHeading';
import SmallHeading from 'components/SmallHeading';


export default function Form() {

    const [ instructions, setInstructions ] = useState([]);
    const [currentId, setCurrentId] = useState(1);
    useEffect(() => {
        apiHelper.get({ "url": `${baseUrl}/api/instructions_from_krishna` })
        .then(response => {
            if (response.status) {
                console.log(response.json);
                setInstructions(response.json);
            } else { console.log(response.json); }
        });
    }, []);

    const formErrorState = useRef({});
    const printState = useRef({ "printCharacteristic": null, "index": 0,
        "data": null, "canvas": null, "server": null });
    const debounceId = useRef(null)


    var  debounceFunction  =  function (func, delay) {
        // Cancels the setTimeout method execution
        clearTimeout(debounceId.current);
        // Executes the func after delay time.
        debounceId.current  =  setTimeout(func, delay);
    }

    

    const checkForFormErrors = function(field, isError) {
        formErrorState.current[field] = isError
        return isError;
    }

    const handleError = function(error) {
        console.log(error)
        printState.current.printCharacteristic = null
    }

    const getDarkPixel = function (imageData, x, y) {
        // Return the pixels that will be printed black
        let red = imageData[(printState.current.canvas.width * y + x) * 4]
        let green = imageData[(printState.current.canvas.width * y + x) * 4 + 1]
        let blue = imageData[(printState.current.canvas.width * y + x) * 4 + 2]
        let alpha = imageData[(printState.current.canvas.width * y + x) * 4 + 3]
        if (alpha < 10) return 0
        return red + green + blue > 730 ? 0 : 1
    }
    const getImagePrintData = function(imageData) {
        if (imageData == null) {
          console.log('No image to print!')
          return new Uint8Array([])
        }
        // Each 8 pixels in a row is represented by a byte
        let printData = new Uint8Array((printState.current.canvas.width / 8) * printState.current.canvas.height + 8)
        let offset = 0
        // Set the header bytes for printing the image
        printData[0] = 29 // Print raster bitmap
        printData[1] = 118 // Print raster bitmap
        printData[2] = 48 // Print raster bitmap
        printData[3] = 0 // Normal 203.2 DPI
        printData[4] = printState.current.canvas.width / 8 // Number of horizontal printState.current.data bits (LSB)
        printData[5] = 0 // Number of horizontal printState.current.data bits (MSB)
        printData[6] = printState.current.canvas.height % 256 // Number of vertical printState.current.data bits (LSB)
        printData[7] = printState.current.canvas.height / 256 // Number of vertical printState.current.data bits (MSB)
        offset = 7
        // Loop through image rows in bytes
        for (let i = 0; i < printState.current.canvas.height; ++i) {
          for (let k = 0; k < printState.current.canvas.width / 8; ++k) {
            let k8 = k * 8
            //  Pixel to bit position mapping
            printData[++offset] =
              getDarkPixel(imageData, k8 + 0, i) * 128 +
              getDarkPixel(imageData, k8 + 1, i) * 64 +
              getDarkPixel(imageData, k8 + 2, i) * 32 +
              getDarkPixel(imageData, k8 + 3, i) * 16 +
              getDarkPixel(imageData, k8 + 4, i) * 8 +
              getDarkPixel(imageData, k8 + 5, i) * 4 +
              getDarkPixel(imageData, k8 + 6, i) * 2 +
              getDarkPixel(imageData, k8 + 7, i)
          }
        }
        return printData
    }
    const sendImageData = function (imageData) {
        printState.current.index = 0
        printState.current.data = getImagePrintData(imageData)
        return new Promise(function (resolve, reject) {
            sendNextImageDataBatch(resolve, reject)
        })
    }

    const sendNextImageDataBatch = function (resolve, reject) {
        // Can only write 512 bytes at a time to the characteristic
        // Need to send the image printState.current.data in 512 byte batches
        if (printState.current.index + 512 < printState.current.data.length) {
          printState.current.printCharacteristic
            .writeValueWithoutResponse(printState.current.data.slice(printState.current.index, printState.current.index + 512))
            .then(() => {
              printState.current.index += 512
              sendNextImageDataBatch(resolve, reject)
            })
            .catch(error => reject(error))
        } else {
          // Send the last bytes
          if (printState.current.index < printState.current.data.length) {
            printState.current.printCharacteristic
              .writeValue(printState.current.data.slice(printState.current.index, printState.current.data.length))
              .then(() => {
                resolve()
              })
              .catch(error => reject(error))
          } else {
            resolve()
          }
        }
    }

    const connectToBluetooth = function () {
        navigator.bluetooth
        .requestDevice({
            filters: [{
                services: ['000018f0-0000-1000-8000-00805f9b34fb']
            }]
        })
        .then(device => {
            console.log('> Found ' + device.name)
            console.log('Connecting to GATT Server...')
            const x = device.gatt.connect()
            return x
        })
        .then(server => {
            printState.current.server = server;
            console.log(server);
            console.log(server.connected);
            return server.getPrimaryService('000018f0-0000-1000-8000-00805f9b34fb')
        })
        .then(service =>
            service.getCharacteristic('00002af1-0000-1000-8000-00805f9b34fb')
        )
        .then(characteristic => {
            // Cache the characteristic
            printState.current.printCharacteristic = characteristic
            notify.success({ "description": "Connected successfully !!" })
        })
        .catch(handleError)
    }

    const onClickSave = function() {
        for (const f in formErrorState.current) {
            if (formErrorState.current[f]) {
                notify.error({ description: 'Please fill all details' });
                return;
            }
        }

        if (!window.AndroidApp &&
                (!printState.current.printCharacteristic ||
                !(printState.current.server && printState.current.server.connected))) {
            notify.error({ description: 'Printer not connected' });
        }
        
            notify.success({ "description": "You touched Krishna's lotus feet!" })

            const currTime = new Date();
            var dateStr = currTime.toLocaleString('en-IN', {
                month: 'long',
                day: '2-digit'
            })
            var timeStr = currTime.toLocaleString('en-IN', {
                hour12: true,
                hour: '2-digit',
                minute: '2-digit'
            })
            // printing late slip after 4:30 PM
            // adding place for signature and FOLK Guide name
            // let lateSlipStr = '';
            let signatureStr = `<div style="margin-top: 18mm; align-self: flex-start;
                    padding-right: 10mm; font-size: 6mm;">Your dearmost friend and well-wisher</div>
                <div style="margin-top: 2mm; margin-bottom: 5mm;
                    align-self: flex-start;
                    padding-right: 10mm; font-size: 5.5mm;">Krishna</div>`;
            

            document.querySelector('body').classList.add('print')
            document.getElementById('print-content').innerHTML = `
                    <div style="text-align: center;">------------</div>
                    <div>
                        <div style="display: flex; flex-wrap: nowrap; margin-top: 7mm; text-align: center;">
                            <div><strong>${instructions[currentId].main}</strong>
                            <br/>
                            ${instructions[currentId].subtitle}</div>
                        </div>
                    </div>
                    ${signatureStr}

                `
            setCurrentId((currentId + 1) % instructions.length);
            document.getElementById('print-slip').classList.add('print')
            // window.print();
            const printNode = document.getElementById('print-slip')
            console.log(printNode.offsetHeight);
            html2canvas(printNode, { width: printNode.offsetWidth, height: printNode.offsetHeight + 100, scale: 1 }).then(
                c => {
                    printState.current.canvas = c
                    let context = printState.current.canvas.getContext('2d')
                    const imageDataUrl = c.toDataURL("image/jpeg").split(';base64,')[1]
                    console.log(imageDataUrl)
                    if (window.AndroidApp) {
                        window.AndroidApp.printPrasadam(imageDataUrl);
                    } else {
                        let imageData = context.getImageData(
                            0,
                            0,
                            c.width,
                            c.height
                        ).data
                        sendImageData(imageData).catch(handleError)
                    }
                }
            )
            document.querySelector('body').classList.remove('print')
            document.getElementById('print-slip').classList.remove('print')
        
    }

    // useEffect(function() {
    //     loadMobileNoOptions('');
    // }, [])
    console.log(window.screen.height)
    return (
        <div style={{ position: 'fixed', width: '100%', height: '100%' }}>
            { !window.AndroidApp &&
                        <Button sx={{ marginTop: '15px' }} onClick={() => connectToBluetooth()}
                                variant="outlined">Connect Printer</Button> }
        
    <img id="image" alt='lotus feet of Krishna' src='/images/LotusFeet.jpg' style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover' 
            }} ></img>
            <button 
            style={{ 
        position: 'absolute', 
        bottom: 0, 
        left: window.innerWidth / 2 - window.innerHeight / 2, // adjust to center horizontally
        width: window.innerHeight, // set width equal to the height of the screen
        height: window.innerHeight, // set height equal to the height of the screen,
        borderRadius: '50%',
        opacity: 0 // makes the button visible
    }}
            onClick={() => { onClickSave() }}
        ></button>
        {!window.AndroidApp &&<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center',
            padding: '15px' }}>
                <ColumnBox>
                    <div style={{ alignSelf: 'center' }} id="print-slip">
                        {/* <div style={{ textAlign: 'center' }}>
                            <img id="image" style={{ width: '45mm' }} src='/images/folklogo.png'></img>
                        </div>
                        <div style={{ fontSize: '6.2mm', fontWeight: 700, letterSpacing: '0.15em',
                            textAlign: 'center', marginTop: '7mm' }}>FOLK ATTENDANCE SLIP</div> */}
                        <div style={{display: 'flex',
                                'flexDirection': 'column',
                                'justifyContent': 'flex-start',
                                'alignItems': 'center',
                                'background': 'white',
                                'fontSize': '6mm',
                                'letterSpacing': '0.15em',
                                'paddingTop': '1mm',
                                'width': '360px'}} id="print-content"></div>
                    </div>
                </ColumnBox>
            </Box>}
            
        </div>
        
    );
}
